<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Stock Overview" :loading="loading" :avatarImage="avatarImage" :avatarCircleMode="false">
          <h4 v-show="stockData.code">{{stockData.code}} - {{stockData.name}}</h4>
          <p>
            <span class="label label-stock-glass mr-2" v-if="stockTypeIsGlass">Glass</span>
            <span class="label label-stock-service mr-2" v-if="stockTypeIsGlassService">Glass Service</span>
            <span class="label label-stock-template mr-2" v-if="stockTypeIsTemplate">Template</span>
            <span class="label label-stock-assembly mr-2" v-if="stockTypeIsAssembly">Assembly</span>
            <span class="label label-stock-general mr-2" v-if="stockTypeIsGeneral">General</span>
            <span class="label label-stock-service" v-if="stockTypeIsGlass">{{getLookupName(glassTypeOptions, stockData.glassType)}}</span>
          </p>
          
          <OverviewBoxInfoItem icon="fa-layer-group" v-if="stockData.stockCategory">{{stockData.stockCategory.name}}</OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-ruler" v-if="stockTypeIsGlass">{{stockData.thickness}}mm</OverviewBoxInfoItem>

          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="stockData.isActive" class="status-active">Active</span>
              <span v-if="!stockData.isActive" class="status-archive">Archived</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{stockData.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{stockData.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>
      </div>
      <div class="col-sm-10">
        <TabbedBox :loading="loading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-summary">
              <i class="far fa-analytics"></i> Summary
            </Tab>
            <Tab tabid="tab-stockitem" ref="stockItemTab">
              <i class="far fa-inventory"></i> Stock Item
            </Tab>
            <Tab tabid="tab-glass-pricing" v-if="stockTypeIsGlass && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockPricing)">
              <i class="far fa-money-check-edit-alt"></i> Glass Pricing
            </Tab>
            <Tab tabid="tab-service-pricing" v-if="stockTypeIsGlassService && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockPricing)">
              <i class="far fa-money-check-edit-alt"></i> Service Pricing
            </Tab>
            <Tab tabid="tab-general-pricing" v-if="stockTypeIsGeneral && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockPricing)">
              <i class="far fa-money-check-edit-alt"></i> General Pricing
            </Tab>
            <Tab tabid="tab-assembly-pricing" v-if="stockTypeIsAssembly && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockPricing)">
              <i class="far fa-money-check-edit-alt"></i> Assembly Pricing
            </Tab>
            <Tab tabid="tab-glass-stock-levels" v-if="stockTypeIsGlass && stockData.isTrackingQuantity">
              <i class="far fa-analytics"></i> Stock Levels
            </Tab>
            <Tab tabid="tab-general-stock-levels" v-if="stockTypeIsGeneral && stockData.isTrackingQuantity">
              <i class="far fa-analytics"></i> Stock Levels
            </Tab>
            <Tab tabid="tab-glass-stock-transactions" v-if="(stockTypeIsGlass || stockTypeIsGeneral) && stockData.isTrackingQuantity && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockTransaction)">
              <i class="far fa-exchange"></i> Stock Transactions
            </Tab>
            <Tab tabid="tab-glass-stock-management" v-if="stockTypeIsGlass && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockManagement) && stockData.isTrackingQuantity">
              <i class="far fa-warehouse-alt"></i> Stock Management
            </Tab>
            <Tab tabid="tab-stock-supplier-pricing" v-if="(stockTypeIsGlassService || stockTypeIsGeneral || stockTypeIsAssembly) && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockManagement)">
              <i class="far fa-industry"></i> Supplier Pricing
            </Tab>
            <Tab tabid="tab-template" v-if="stockTypeIsTemplate">
              <i class="far fa-list-alt"></i> Template
            </Tab>
            <Tab tabid="tab-assembly" v-if="stockTypeIsAssembly">
              <i class="far fa-list-alt"></i> Assembly
            </Tab>
            <Tab tabid="tab-production" v-if="(stockTypeIsGlass || stockTypeIsGlassService) && (userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionConfig) || userHasReadPermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration))">
              <i class="far fa-tools"></i> Production
            </Tab>
            <Tab tabid="tab-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-activity-log">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-summary">
              <div v-if="stockData.id">
                <h1 class="mt-2 mb-4">Overview for {{stockData.name}}</h1>
                <AnalyticsStockItemDashboard :stockId="stockData.id" :stockType="stockData.type" :uomType="stockData.uomType" />
              </div>
            </TabPage>
            <TabPage tabid="tab-stockitem">
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stock)" class="float-right">
                    <DropdownMenuItem @click="editStock" icon="fa-pencil">Edit Stock</DropdownMenuItem>
                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="archiveStock" v-if="stockData.isActive" icon="fa-archive">Archive Stock</DropdownMenuItem>
                    <DropdownMenuItem @click="restoreStock" v-if="!stockData.isActive" icon="fa-undo">Restore Stock</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{stockData.name}}</DataItem>
                  <DataItem title="Code">{{stockData.code}}</DataItem>
                  <DataItem title="Category">
                    <span v-for="(category, index) in stockData.categoryTree" :key="index">
                      <i class="fas fa-caret-right" v-if="index > 0"></i> {{category.name}}&nbsp;
                    </span>
                  </DataItem>
                  <DataItem title="Notes">{{stockData.note}}</DataItem>
                  
                  <DataItem title="Unit of Measure"><span v-html="uomDisplay"></span></DataItem>
                  <DataItem title="Weight" v-if="stockTypeIsGeneral">{{stockData.weight}}<span v-if="stockData.weight">Kg</span></DataItem>

                  <template v-if="stockTypeIsGeneral">
                    <DataItem title="Tracking Quantities">{{stockData.isTrackingQuantity ? 'Yes' : 'No'}}</DataItem>
                    <DataItem title="Task Completable">{{stockData.isTaskCompletable ? 'Yes' : 'No'}}</DataItem>
                    <DataItem title="Production Completable">{{stockData.isProductionCompletable ? 'Yes' : 'No'}}</DataItem>
                    <DataItem v-if="stockData.isTrackingQuantity" title="Quantity"><span v-html="stockData.quantity + ' ' + uomDisplayUnits"></span></DataItem>
                    <DataItem title="Fixed Utilisation Rate" v-if="stockData.averageStockUtilisationPercentage > 0.0 && stockData.isTrackingQuantity">{{formatPercent(stockData.averageStockUtilisationPercentage)}}</DataItem>
                  </template>

                  <template v-if="stockTypeIsGlass">
                    <DataItem title="Glass Type">{{getLookupName(glassTypeOptions, stockData.glassType)}}</DataItem>
                    <DataItem title="Glass Thickness">{{stockData.thickness | formatLength}}</DataItem>
                    <DataItem title="Fixed Utilisation Rate" v-if="stockData.averageStockUtilisationPercentage > 0.0 && stockData.isTrackingQuantity">{{formatPercent(stockData.averageStockUtilisationPercentage)}}</DataItem>
                  </template>
                </template>
              </DataPageArea>
         
            </TabPage>
            <TabPage tabid="tab-glass-pricing">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockPricing) && !editingPricing" class="float-right">
                    <DropdownMenuItem @click="editingPricing = true" icon="fa-pencil">Edit Pricing</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Sales Pricing</h3>
                </div>
              </div>

              <FormGroup>
                <SalesGlassPricingEditor
                  :priceGroupList="stockData.glassSalesPriceList"
                  :uomType="stockData.uomType"
                  @update="stockData.glassSalesPriceList = $event"
                  :editing="editingPricing" />

                <div v-if="editingPricing">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="stockPricingSubmitting" @cancelled="cancelPricingEditing" @submitted="submitPricingEdit"></FormSubmit>
                </div>
              </FormGroup>

            </TabPage>

            <TabPage tabid="tab-service-pricing">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockPricing) && !editingPricing" class="float-right">
                    <DropdownMenuItem @click="editingPricing = true" icon="fa-pencil">Edit Pricing</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Pricing</h3>
                </div>
              </div>

              <FormGroup>
                <SalesServicePricingEditor
                  :priceGroupList="stockData.serviceSalesPriceList"
                  :uomType="stockData.uomType"
                  @update="stockData.serviceSalesPriceList = $event"
                  :editing="editingPricing" 
                />

                <div v-if="editingPricing">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="stockPricingSubmitting" @cancelled="cancelPricingEditing" @submitted="submitPricingEdit"></FormSubmit>
                </div>
              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-general-pricing">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockPricing) && !editingPricing" class="float-right">
                    <DropdownMenuItem @click="editingPricing = true" icon="fa-pencil">Edit Pricing</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Pricing</h3>
                </div>
              </div>

              <FormGroup>
                <SalesGeneralPricingEditor 
                  :priceList="stockData.generalSalesPriceList"
                  :uomType="stockData.uomType"
                  @update="stockData.generalSalesPriceList = $event"
                  :editing="editingPricing" />

                <div v-if="editingPricing">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="stockPricingSubmitting" @cancelled="cancelPricingEditing" @submitted="submitPricingEdit"></FormSubmit>
                </div>
              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-assembly-pricing">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockPricing) && !editingPricing" class="float-right">
                    <DropdownMenuItem @click="editingPricing = true" icon="fa-pencil">Edit Pricing</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Pricing</h3>
                </div>
              </div>

              <FormGroup>
                <SalesAssemblyPricingEditor 
                  :priceList="stockData.assemblySalesPriceList"
                  :uomType="stockData.uomType"
                  @update="stockData.assemblySalesPriceList = $event"
                  :editing="editingPricing" />

                <div v-if="editingPricing">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="stockPricingSubmitting" @cancelled="cancelPricingEditing" @submitted="submitPricingEdit"></FormSubmit>
                </div>
              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-template" v-if="stockTypeIsTemplate">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stock) && !editingTemplate" class="float-right">
                    <DropdownMenuItem @click="editingTemplate = true" icon="fa-pencil">Edit Template</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Template</h3>
                </div>
              </div>

              <FormGroup>
                <StockTemplateEditor
                  :templateList="stockData.templateList"
                  :editing="editingTemplate"
                  @update="stockData.templateList = $event" />

                <div v-if="editingTemplate">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="stockTemplateSubmitting" @cancelled="cancelTemplateEditing" @submitted="submitTemplateEdit"></FormSubmit>
                </div>
              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-assembly" v-if="stockTypeIsAssembly">
              <div class="row">
                <div class="col-lg-12">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stock) && !editingAssembly" class="float-right">
                    <DropdownMenuItem @click="editingAssembly = true" icon="fa-pencil">Edit Assembly</DropdownMenuItem>
                  </ActionDropMenu>
                  <h3>Assembly</h3>
                </div>
              </div>

              <FormGroup>
                <StockAssemblyEditor
                  :stock="stockData"
                  v-model="stockData.assemblyData"
                  :editing="editingAssembly" />

                <div v-if="editingAssembly">
                  <FormHorizontalRule />
                  <FormSubmit label="Update" :submitting="stockAssemblySubmitting" @cancelled="cancelAssemblyEditing" @submitted="submitAssemblyEdit"></FormSubmit>
                </div>
              </FormGroup>
            </TabPage>

            <TabPage tabid="tab-production" v-if="(stockTypeIsGlass || stockTypeIsGlassService) && (userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionConfig) || userHasReadPermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration))">
              <NestedTabbedBox>
                <template v-slot:tablist>
                  <Tab :first="true" tabid="tab-production-settings">
                    <i class="far fa-cogs"></i> Production Settings
                  </Tab>
                  <Tab tabid="tab-production-links">
                    <i class="far fa-tools"></i> Production Links
                  </Tab>
                  <Tab tabid="tab-production-routing">
                    <i class="far fa-exchange"></i> Production Routing
                  </Tab>
                  <Tab tabid="tab-production-order-settings" v-if="stockTypeIsGlass">
                    <i class="far fa-shopping-cart"></i> Order Settings
                  </Tab>
                  <Tab tabid="tab-production-edgework-settings" v-if="stockTypeIsGlass">
                    <i class="far fa-border-outer"></i> Edgework Settings
                  </Tab>
                </template>
                <template v-slot:tabcontent>
                  <TabPage :first="true" tabid="tab-production-settings">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stock) && !editingProductionSettings" class="float-right">
                          <DropdownMenuItem @click="editingProductionSettings = true" icon="fa-pencil">Edit Production Settings</DropdownMenuItem>
                        </ActionDropMenu>
                        <ContentTitle>Production Settings</ContentTitle>
                        <ContentSubTitle>Stock will use production and optimiser configurations by default, but the settings can be set below for stock specific control</ContentSubTitle>
                      </div>
                    </div>

                    <StockProductionSettingsEditor v-model="stockData" :editing="editingProductionSettings" />

                    <div v-if="editingProductionSettings">
                      <FormHorizontalRule />
                      <FormSubmit label="Update" :submitting="editingProductionSettingsSubmitting" @cancelled="cancelProductionSettingEditing" @submitted="submitProductionSettingEdit"></FormSubmit>
                    </div>
                  </TabPage>

                  <TabPage tabid="tab-production-links">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig) && !editingProductionLinks" class="float-right">
                          <DropdownMenuItem @click="editingProductionLinks = true" icon="fa-pencil">Edit Production Links</DropdownMenuItem>
                        </ActionDropMenu>
                        <ContentTitle>Production Links</ContentTitle>
                        <ContentSubTitle>Stock will use production configurations by default, but the stage and stations can be set below for stock specific control over production</ContentSubTitle>
                      </div>
                    </div>

                    <FormGroup>
                      <ProductionGlassStockLinkEditor 
                        v-if="stockTypeIsGlass"
                        :stockId="stockData.id"
                        v-model="productionConfigData"
                        :editing="editingProductionLinks" />

                      <ProductionGlassServiceStockLinkEditor 
                        v-if="stockTypeIsGlassService"
                        :stockId="stockData.id"
                        v-model="productionConfigData"
                        :editing="editingProductionLinks" />

                      <div v-if="editingProductionLinks">
                        <FormHorizontalRule />
                        <FormSubmit label="Update" :submitting="stockProductionLinksSubmitting" @cancelled="cancelProductionLinkEditing" @submitted="submitProductionLinkEdit"></FormSubmit>
                      </div>
                    </FormGroup>
                  </TabPage>

                  <TabPage tabid="tab-production-routing">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig) && !editingProductionRouting" class="float-right">
                          <DropdownMenuItem @click="editingProductionRouting = true" icon="fa-pencil">Edit Production Routing</DropdownMenuItem>
                        </ActionDropMenu>
                        <ContentTitle>Production Routing</ContentTitle>
                        <ContentSubTitle>Stock will use production configurations by default, but the stations can be set below for stock specific control over production stages</ContentSubTitle>
                        <ContentSubTitle>Routing overrides below will only apply to production items that are required at the applicable production stage</ContentSubTitle>
                      </div>
                    </div>

                    <FormGroup>
                      <ProductionGlassStockRouteEditor 
                        v-if="stockTypeIsGlass"
                        :stockId="stockData.id"
                        v-model="productionConfigData"
                        :editing="editingProductionRouting" />

                      <div v-if="editingProductionRouting">
                        <FormHorizontalRule />
                        <FormSubmit label="Update" :submitting="stockProductionRoutingSubmitting" @cancelled="cancelProductionRouteEditing" @submitted="submitProductionRouteEdit"></FormSubmit>
                      </div>
                    </FormGroup>
                  </TabPage>

                  <TabPage tabid="tab-production-order-settings">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration) && !editingOrderConfig" class="float-right">
                          <DropdownMenuItem @click="editingOrderConfig = true" icon="fa-pencil">Edit Order Settings</DropdownMenuItem>
                        </ActionDropMenu>
                        <ContentTitle>Order Settings</ContentTitle>
                        <ContentSubTitle>Stock will use edgework configurations by default, but the edgework can be set below for stock specific control over order editor</ContentSubTitle>
                      </div>
                    </div>

                    <FormGroup>
                      <DefaultEdgeworkLinkEditor :stockId="stockData.id" v-model="orderEditorConfigData" :editing="editingOrderConfig" />

                      <div v-if="editingOrderConfig">
                        <FormHorizontalRule />
                        <FormSubmit label="Update" :submitting="stockOrderEditorSubmitting" @cancelled="cancelOrderEditorEditing" @submitted="submitOrderEditorEdit"></FormSubmit>
                      </div>
                    </FormGroup>
                  </TabPage>

                  <TabPage tabid="tab-production-edgework-settings">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.OrderEditorPermissionTypes.orderEditorConfiguration) && !orderEditorEdgeworkSettings.editing" class="float-right">
                          <DropdownMenuItem @click="editOrderEditorEdgework" icon="fa-pencil">Edit Edgework Settings</DropdownMenuItem>
                        </ActionDropMenu>
                        <ContentTitle>Edgework Settings</ContentTitle>
                        <ContentSubTitle>Stock will use edgework allowances in order editing configuration by default, but the allowances can be set below for stock specific control over order editor</ContentSubTitle>
                      </div>
                    </div>

                    <FormGroup :form="orderEditorEdgeworkSettings.form">
                      <StockEdgeworkAllowanceEditor v-model="orderEditorEdgeworkSettings.edgeworkAllowanceData" :editing="orderEditorEdgeworkSettings.editing" />

                      <div v-if="orderEditorEdgeworkSettings.editing">
                        <FormHorizontalRule />
                        <FormSubmit label="Update" :submitting="orderEditorEdgeworkSettings.submitting" @cancelled="cancelOrderEditorEdgeworkEditing" @submitted="submitOrderEditorEdgeworkEditing"></FormSubmit>
                      </div>
                    </FormGroup>

                  </TabPage>

                </template>
              </NestedTabbedBox>  
            </TabPage>

            <TabPage tabid="tab-general-stock-levels" v-if="stockTypeIsGeneral && stockData.isTrackingQuantity">
              <SummaryBar>
                <SummaryBarItem :title="`${formatDecimal(stockData.quantity, 2, true)}${uomDisplayUnits}`" :description="`Total ${uomDisplay}`" />
              </SummaryBar>

              <div class="row mt-3">
                <div class="col-lg-12">
                  <ContentTitle>Stock Levels</ContentTitle>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-12">
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Bin Barcode</th>
                        <th>Bin</th>
                        <th>Location</th>
                        <th>Quantity</th>
                        <th>Actions</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <template v-for="binLink in stockData.stockLevelSummary.inventoryBinLinks">
                        <tr :key="binLink.id">
                          <td class="font-weight-bold">{{binLink.inventoryBin.barcode}}</td>
                          <td>{{binLink.inventoryBin.code}}</td>
                          <td>{{binLink.inventoryBin.accountLocation.name}}</td>
                          <td><UOMValue :value="binLink.quantity" :uomType="stockData.uomType" /></td>
                          <td>
                            <ActionDropMenu :tableAction="true" :forceDropdown="true">
                              <DropdownMenuItem @click="viewInventoryBinContents(binLink.inventoryBin)" icon="fa-layer-group">View Bin Contents</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                      </template>
                    </template>

                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-glass-stock-levels" v-if="stockTypeIsGlass && stockData.isTrackingQuantity">
              <SummaryBar>
                <SummaryBarItem :title="formatInteger(totalSheets, false, true)" :description="`Sheet${totalSheets == 1 ? '' : 's'}`" />
                <SummaryBarItem :title="formatInteger(totalBlocks, false, true)" :description="`Block${totalBlocks == 1 ? '' : 's'}`" />
                <SummaryBarItem :title="formatInteger(totalOffcuts, false, true)" :description="`Offcut${totalOffcuts == 1 ? '' : 's'}`" />
                <SummaryBarItem :title="`${formatInteger(totalGlassArea, false, true)}m&sup2;`" description="Total m&sup2;" />
              </SummaryBar>

              <div class="row pt-3">
                <div class="col-lg-12">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem @click="showEmptyGlassLevels = true" icon="fa-eye" v-if="!showEmptyGlassLevels">Show Empty Levels</DropdownMenuItem>
                    <DropdownMenuItem @click="showEmptyGlassLevels = false" icon="fa-eye-slash" v-if="showEmptyGlassLevels">Hide Empty Levels</DropdownMenuItem>
                  </ActionDropMenu>
                  <ContentTitle>Sheets</ContentTitle>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-12">
                  <StaticDataTable class="static-table-list table-grouped-list" :hover="true" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Size (HxW) mm</th>
                        <th v-if="isStockUsingGrain">Grain</th>
                        <th>Level Size</th>
                        <th>Blocks</th>
                        <th>Total Sheets</th>
                        <th><Tooltip label="Stock sheets required for active orders" helpStyle>Stock Sheets Ordered</Tooltip></th>
                        <th><Tooltip label="Stock allocated to optimiser sheets" helpStyle>Optimiser Sheets</Tooltip></th>
                        <th><Tooltip label="Sheets remaining after stock orders and optimisation" helpStyle>Available</Tooltip></th>
                        <th><Tooltip label="Incoming stock on order via purchase orders" helpStyle>Incoming</Tooltip></th>
                        <th>Actions</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <template v-for="(glassSize, glassSizeIndex) in stockData.glassSizeList">
                        <tr v-if="glassSizeIndex > 0" class="item-spacer" :key="`item-${glassSizeIndex}-spacer`">
                          <td> </td>
                          <td v-if="isStockUsingGrain"> </td>
                          <td colspan="8"> </td>
                        </tr>
                        
                        <tr :key="glassSize.id" class="item-data order-item-row-group-glass">
                          <td class="font-weight-bold">{{glassSize.height}} x {{glassSize.width}}</td>
                          <td v-if="isStockUsingGrain">
                            {{formatLookupName(glassSize.grainDirection, StockGrainDirectionTypeLookup)}}
                          </td>
                          <td></td>
                          <td class="font-weight-bold">{{glassSize.summary.totalBlocks}}</td>
                          <td class="font-weight-bold">{{glassSize.summary.totalSheetStock - glassSize.summary.totalOffcuts}}</td>
                          <td class="font-weight-bold">{{glassSize.summary.totalOrderEntryStock - glassSize.summary.totalOffcutOrderEntryStock}}</td>
                          <td class="font-weight-bold">{{glassSize.summary.totalOptimiserAllocated - glassSize.summary.totalOffcutOptimiserAllocated}}</td>
                          <td class="font-weight-bold">{{glassSize.summary.totalAvailable - glassSize.summary.totalOffcutAvailable}}</td>
                          <td class="font-weight-bold">{{glassSize.summary.totalOnOrder}}</td>
                          <td>
                            <ActionDropMenu :tableAction="true">
                              <DropdownMenuItem @click="displayGlassSizeBlocks(glassSize)" icon="fa-box">Show blocks</DropdownMenuItem>
                              <DropdownMenuItem @click="displayGlassSizeInventoryBins(glassSize)" icon="fa-inventory">Show Inventory Bins</DropdownMenuItem>
                              <DropdownMenuItem @click="displayGlassSizeOrders(glassSize)" icon="fa-shopping-cart" v-if="userHasReadPermission(PermissionTypes.OrderPermissionTypes.order)">Show Orders</DropdownMenuItem>
                              <DropdownMenuItem @click="displayGlassSizePurchaseOrders(glassSize)" v-if="userHasReadPermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder)" icon="fa-dolly-flatbed">Show Purchase Orders</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                        <template v-for="accLocation in filteredAccountList(glassSize)">
                          <tr :key="`acc-${glassSize.id}-${accLocation.id}`" class="item-data order-item-row-group-glass">
                              <td>
                                <i class="fas fa-caret-right ml-4"></i> {{accLocation.name}}
                              </td>
                              <td v-if="isStockUsingGrain"></td>
                              <td colspan="8"></td>
                            </tr>
                          <template v-for="glassLevel in filteredAccountGlassLevelList(accLocation)">
                            <tr :key="`level-${glassLevel.id}-${accLocation.id}`" v-if="!glassLevel.isOffcut" class="item-data order-item-row-group-glass">
                              <td></td>
                              <td v-if="isStockUsingGrain"></td>
                              <td>
                                {{glassLevel.height}} x {{glassLevel.width}}
                              </td>
                              <td>{{glassLevel.totalBlocks}}</td>
                              <td>{{glassLevel.totalSheetStock}}</td>
                              <td>{{glassLevel.totalOrderEntryStock}}</td>
                              <td>{{glassLevel.totalOptimiserAllocated}}</td>
                              <td>{{glassLevel.totalAvailable}}</td>
                              <td>{{glassLevel.totalOnOrder}}</td>
                              <td></td>
                            </tr>
                          </template>
                        </template>
                      </template>
                      <tr key="totals" class="border-top font-weight-bold static-datatable-row-nostripe">
                        <td>Totals</td>
                        <td v-if="isStockUsingGrain"></td>
                        <td></td>
                        <td>{{totalBlocks}}</td>
                        <td>{{totalSheets}}</td>
                        <td>{{totalOrderEntryStock - totalOffcutOrderEntryStock}}</td>
                        <td>{{totalOptimiserAllocated - totalOffcutOptimiserAllocated}}</td>
                        <td>{{totalAvailable - totalOffcutAvailable}}</td>
                        <td>{{totalOnOrder}}</td>
                        <td></td>
                      </tr>
                    </template>
                  </StaticDataTable>
                  <p class="text-center font-italic" v-if="stockData.glassSizeList.length == 0">No glass levels found</p>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-12">
                  <div class="hr-line-dashed" />
                </div>
              </div>
              <div class="row pt-2">
                <div class="col-lg-12">
                  <h3>Offcuts</h3>
                  <StaticDataTable class="static-table-list">
                    <template v-slot:header>
                      <tr>
                        <th>Size (HxW) mm</th>
                        <th>Barcode</th>
                        <th><Tooltip label="Original sheet size the offcut was from" helpStyle>Source Sheet Size</Tooltip></th>
                        <th v-if="isStockUsingGrain">Grain</th>
                        <th><Tooltip label="Stock sheets required for active orders" helpStyle>Stock Sheets Ordered</Tooltip></th>
                        <th><Tooltip label="Stock allocated to optimiser sheets" helpStyle>Optimiser Sheets</Tooltip></th>
                        <th><Tooltip label="Sheets remaining after stock orders and optimisation" helpStyle>Available</Tooltip></th>
                        <th>Bin</th>
                        <th>Location</th>
                        <th>Actions</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <template v-for="glassSize in stockData.glassSizeList">
                        <template v-for="glassLevel in glassSize.glassLevels">
                          <tr :key="'level-offcut-' + glassLevel.id" v-if="glassLevel.isOffcut && glassLevel.isOffcutAvailable">
                            <td class="font-weight-bold">{{glassLevel.height}} x {{glassLevel.width}}</td>
                            <td>{{glassLevel.barcode}}</td>
                            <td>{{glassSize.height}} x {{glassSize.width}}</td>
                            <td v-if="isStockUsingGrain">{{formatLookupName(glassSize.grainDirection, StockGrainDirectionTypeLookup)}}</td>
                            <td>{{glassLevel.summary.totalOrderEntryStock}}</td>
                            <td>{{glassLevel.summary.totalOptimiserAllocated}}</td>
                            <td>{{glassLevel.summary.totalAvailable}}</td>
                            <td>{{glassLevel.inventoryBin ? glassLevel.inventoryBin.code : '-'}}</td>
                            <td>{{glassLevel.inventoryBin ? glassLevel.inventoryBin.accountLocation.name : '-'}}</td>
                            <td>
                              <ActionDropMenu :tableAction="true" :forceDropdown="true">
                                <DropdownMenuItem @click="printGlassOffcutLabel(glassLevel)" icon="fa-print">Print Offcut Label</DropdownMenuItem>
                              </ActionDropMenu>
                            </td>
                          </tr>
                        </template>
                      </template>
                      <tr key="totals" class="border-top font-weight-bold static-datatable-row-nostripe">
                        <td>Totals</td>
                        <td>{{totalOffcuts}}</td>
                        <td></td>
                        <td v-if="isStockUsingGrain"></td>
                        <td>{{totalOffcutOrderEntryStock}}</td>
                        <td>{{totalOffcutOptimiserAllocated}}</td>
                        <td>{{totalOffcutAvailable}}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </template>
                  </StaticDataTable>
                  <p class="text-center font-italic" v-if="stockData.glassOffcutList.length == 0">No offcuts found</p>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-glass-stock-transactions" v-if="(stockTypeIsGlass || (stockTypeIsGeneral && stockData.isTrackingQuantity)) && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockTransaction)">
              <div class="row">
                <div class="col-12">
                  <StockTransactionDataTable :filterStockId="stockData.id">
                    <template v-slot:title>
                      <h3>Stock transactions</h3>
                    </template>
                  </StockTransactionDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-glass-stock-management" v-if="stockTypeIsGlass && userHasReadPermission(PermissionTypes.InventoryPermissionTypes.stockManagement)">
              <NestedTabbedBox>
                <template v-slot:tablist>
                  <Tab :first="true" tabid="tab-glass-stock-management-1">
                    <i class="far fa-analytics"></i> Stock Value
                  </Tab>
                  <Tab tabid="tab-glass-stock-management-2">
                    <i class="far fa-container-storage"></i> Bulk Glass
                  </Tab>
                  <Tab tabid="tab-glass-stock-management-3">
                    <i class="far fa-cogs"></i> Processed Glass
                  </Tab>
                </template>
                <template v-slot:tabcontent>
                  <TabPage :first="true" tabid="tab-glass-stock-management-1">
                    <div class="row">
                      <div class="col-lg-12">
                        <h3>Glass Value</h3>
                      </div>
                    </div>

                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Size (HxW) mm</th>
                          <th class="text-center">Active</th>
                          <th>Total On Hand</th>
                          <th><Tooltip label="Unit price moving average over time" helpStyle>Unit Price (MA)</Tooltip></th>
                          <th>Total Value On Hand</th>
                          <th>Actions</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="glassSize in stockData.glassSizeList" :key="glassSize.id">
                          <td class="font-weight-bold">{{glassSize.height}} x {{glassSize.width}}</td>
                          <td class="text-center" v-html="dataDisplayTableBoolean(glassSize.isActive, { showFalse: true })"></td>
                          <td>{{glassSize.summary.totalArea | formatDecimal(5, true)}} m&sup2;</td>
                          <td>{{glassSize.unitPriceMovingAverage | formatCurrency}}</td>
                          <td class="font-weight-bold">{{glassSize.summary.totalStockOnHandValue | formatCurrency}}</td>
                          <td>
                            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockManagement)" :tableAction="true" :forceDropdown="true">
                              <DropdownMenuItem @click="editGlassSizeMovingAverage(glassSize)" icon="fa-pencil">Adjust Moving Average</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                        <tr class="font-weight-bold static-datatable-row-nostripe border-top" v-if="stockData.glassSizeList.length > 0">
                          <td>Totals</td>
                          <td> </td>
                          <td>{{stockData.stockLevelSummary.totalGlassArea | formatDecimal(5, true)}} m&sup2;</td>
                          <td><Tooltip label="Estimated total stock unit price moving average" helpStyle>~{{stockData.stockLevelSummary.totalUnitPriceAvg | formatCurrency}}</Tooltip></td>
                          <td>{{stockData.stockLevelSummary.totalOnHandValue | formatCurrency}}</td>
                          <td></td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="stockData.glassSizeList.length == 0">
                        No glass sizes found
                      </template>
                    </StaticDataTable>
                  </TabPage>

                  <TabPage tabid="tab-glass-stock-management-2">
                    <div class="row">
                      <div class="col-lg-12">
                        <ActionDropMenu v-if="userHasCreatePermission(PermissionTypes.InventoryPermissionTypes.stockManagement)" class="float-right">
                          <DropdownMenuItem @click="createGlassSize" icon="fa-plus">Add Glass Size</DropdownMenuItem>
                        </ActionDropMenu>
                        <h3>Glass Sizes</h3>
                      </div>
                    </div>

                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Size (HxW) mm</th>
                          <th class="text-center">Active</th>
                          <th>Block Size</th>
                          <th>Container Type</th>
                          <th v-if="isStockUsingGrain">Grain</th>
                          <th><Tooltip label="Number of business days of average usage required to keep in stock. Used for purchasing planning" helpStyle>Days of Stock Required</Tooltip></th>
                          <th><Tooltip label="Maintain level used for alerts via automations" helpStyle>Maintain Level</Tooltip></th>
                          <th><Tooltip label="Indicates if ongoing purchasing is required for this glass size" helpStyle>Purchasing Required?</Tooltip></th>
                          <th><Tooltip label="Indicates if purchasing is paused for this glass size" helpStyle>Purchasing Paused?</Tooltip></th>
                          <th>Preferred Supplier</th>
                          <th v-html="'Est. Landed Cost / ' + uomDisplayUnits"></th>
                          <th>Actions</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="glassSize in stockData.glassSizeList" :key="glassSize.id">
                          <td class="font-weight-bold">{{glassSize.height}} x {{glassSize.width}}</td>
                          <td class="text-center" v-html="dataDisplayTableBoolean(glassSize.isActive, { showFalse: true })"></td>
                          <td>{{glassSize.blockSize}}</td>
                          <td>{{glassSize.stockContainer ? glassSize.stockContainer.code : ''}}</td>
                          <td v-if="isStockUsingGrain">{{formatLookupName(glassSize.grainDirection, StockGrainDirectionTypeLookup)}}</td>
                          <td>{{glassSize.requiredDaysOfStock}}</td>
                          <td>{{glassSize.maintainLevel}}</td>
                          <td><IconBoolean :value="glassSize.isPurchasingRequired" /></td>
                          <td>
                            <span v-if="!isGlassSizePurchasingPaused(glassSize)">-</span>
                            <span v-if="isGlassSizePurchasingPaused(glassSize)">Paused until {{formatDate(glassSize.purchasingPauseExpiryDate)}}</span>
                          </td>
                          <td>
                            <LinkContact v-if="glassSize.preferredSupplier" :id="glassSize.preferredSupplier.id" :name="glassSize.preferredSupplier.name" />
                            <span v-else>-</span>
                          </td>
                          <td>{{glassSize.landedCost | formatCurrency}}</td>
                          <td>
                            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockManagement)" :tableAction="true">
                              <DropdownMenuItem @click="editGlassSize(glassSize)" icon="fa-pencil">Edit Size</DropdownMenuItem>
                              <DropdownMenuItem @click="setGlassSizeActive(glassSize.id, false)" icon="fa-times" v-if="glassSize.isActive">Make Inactive</DropdownMenuItem>
                              <DropdownMenuItem @click="setGlassSizeActive(glassSize.id, true)" icon="fa-check" v-if="!glassSize.isActive">Make Active</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="stockData.glassSizeList.length == 0">
                        No glass sizes found
                      </template>
                    </StaticDataTable>

                    <div class="row my-4">
                      <div class="col-lg-12"></div>
                    </div>

                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <div class="btn-group float-right">
                          <button class="btn btn-xs btn-white" :class="[isContainerMatrixDisplayPrice ? 'active' : '']" @click.prevent="isContainerMatrixDisplayPrice=true">Price</button>
                          <button class="btn btn-xs btn-white" :class="[!isContainerMatrixDisplayPrice ? 'active' : '']" @click.prevent="isContainerMatrixDisplayPrice=false">Est Landed</button>
                        </div>
                        <h3>Supplier Container Pricing Matrix</h3>
                      </div>
                    </div>
                    <div class="row mt-2" v-if="stockData.bulkSupplierMatrix">
                      <div class="col-lg-12">
                        <StockSupplierMatrix
                        :displayCostPrice="isContainerMatrixDisplayPrice"
                        :priceCategoryList="stockData.bulkSupplierMatrix.header"
                        :supplierPriceList="stockData.bulkSupplierMatrix.rows"
                        />
                      </div>
                    </div>
                  </TabPage>
                  <TabPage tabid="tab-glass-stock-management-3">
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Price Group</th>
                          <th>Preferred Supplier</th>
                          <th v-html="'Est. Landed Cost / ' + uomDisplayUnits"></th>
                          <th>Actions</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="priceGroup in stockData.glassProcessedPriceGroupList" :key="priceGroup.id">
                          <td class="font-weight-bold">{{priceGroup.name}}</td>
                          <td>
                            <template v-if="priceGroup.glassPriceGroupSupplier && priceGroup.glassPriceGroupSupplier.preferredSupplier">
                              <router-link v-if="userHasReadPermission(PermissionTypes.ContactPermissionTypes.contact) && priceGroup.glassPriceGroupSupplier.preferredSupplier" :to="`/contacts/${priceGroup.glassPriceGroupSupplier.preferredSupplier.id}`" >{{priceGroup.glassPriceGroupSupplier.preferredSupplier.name}}</router-link>
                              <span v-else>{{priceGroup.glassPriceGroupSupplier.preferredSupplier.name}}</span>
                            </template>
                            <span v-else>-</span>
                          </td>
                          <td>
                            <span v-if="priceGroup.glassPriceGroupSupplier && priceGroup.glassPriceGroupSupplier.preferredSupplier && priceGroup.glassPriceGroupSupplier.preferredSupplier.stockSupplierPrices[0]">
                              {{priceGroup.glassPriceGroupSupplier.preferredSupplier.stockSupplierPrices[0].landedCost | formatCurrency}}
                            </span>
                            <span v-else>-</span>
                          </td>
                          <td>
                            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.InventoryPermissionTypes.stockManagement)" :tableAction="true">
                              <DropdownMenuItem @click="editPriceGroupSupplier(priceGroup)" icon="fa-pencil" label="Edit">Edit Price Group Supplier</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                      </template>
                    </StaticDataTable>

                    <div class="row my-4">
                      <div class="col-lg-12"></div>
                    </div>

                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <div class="btn-group float-right">
                          <button class="btn btn-xs btn-white" :class="[isProcessedMatrixDisplayPrice ? 'active' : '']" @click.prevent="isProcessedMatrixDisplayPrice=true">Price</button>
                          <button class="btn btn-xs btn-white" :class="[!isProcessedMatrixDisplayPrice ? 'active' : '']" @click.prevent="isProcessedMatrixDisplayPrice=false">Est Landed</button>
                        </div>
                        <h3>Supplier Processed Pricing Matrix</h3>
                      </div>
                    </div>
                    <div class="row mt-2" v-if="stockData.processedSupplierMatrix">
                      <div class="col-lg-12">
                        <StockSupplierMatrix
                        :displayCostPrice="isProcessedMatrixDisplayPrice"
                        :priceCategoryList="stockData.processedSupplierMatrix.header"
                        :supplierPriceList="stockData.processedSupplierMatrix.rows"
                        />
                      </div>
                    </div>
                  </TabPage>
                </template>
              </NestedTabbedBox>
            </TabPage>

            <TabPage tabid="tab-stock-supplier-pricing">

              <div v-if="stockTypeIsGlassService">
                <ul class="nav nav-tabs" role="tablist">
                  <Tab v-for="(matrix, index) in stockData.serviceSupplierMatrix" v-bind:first="index == 0" :tabid="`tab-service-supplier-pricing-${index}`" :key="index">
                    {{matrix.name}}
                  </Tab>
                </ul>
                <div class="tab-content">
                  <TabPage v-for="(matrix, index) in stockData.serviceSupplierMatrix" v-bind:first="index == 0" :tabid="`tab-service-supplier-pricing-${index}`" :key="index">

                    <div class="row mt-2">
                      <div class="col-lg-12">
                        <h3>Supplier Pricing Matrix</h3>
                        <StockSupplierMatrix
                          :displayCostPrice="true"
                          :displayPreferredSupplier="true"
                          :priceCategoryList="matrix.header"
                          :supplierPriceList="matrix.rows"
                          @requestSetPreferredSupplier="setServicePreferredSupplier(matrix.stockPriceGroupId, $event)"
                          />
                      </div>
                    </div> 
                  </TabPage>
                </div>
              </div>

              <div v-if="stockTypeIsGeneral">
                <div class="row mt-2">
                  <div class="col-lg-12">
                    <h3>Supplier Pricing Matrix</h3>
                  </div>
                </div>
                <div class="row mt-2" v-if="stockData.generalSupplierMatrix">
                  <div class="col-lg-12">
                    <StockSupplierMatrix
                      :displayCostPrice="true"
                      :displayPreferredSupplier="true"
                      :priceCategoryList="stockData.generalSupplierMatrix.header"
                      :supplierPriceList="stockData.generalSupplierMatrix.rows"
                      @requestSetPreferredSupplier="setGeneralPreferredSupplier($event)"
                    />
                  </div>
                </div>
              </div>

              <div v-if="stockTypeIsAssembly">
                <div class="row mt-2">
                  <div class="col-lg-12">
                    <h3>Supplier Pricing Matrix</h3>
                  </div>
                </div>
                <div class="row mt-2" v-if="stockData.assemblySupplierMatrix">
                  <div class="col-lg-12">
                    <StockSupplierMatrix
                      :displayCostPrice="true"
                      :displayPreferredSupplier="true"
                      :priceCategoryList="stockData.assemblySupplierMatrix.header"
                      :supplierPriceList="stockData.assemblySupplierMatrix.rows"
                      @requestSetPreferredSupplier="setAssemblyPreferredSupplier($event)"
                    />
                  </div>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.stock"
                    :linkId="stockData.id"
                    :permissionResource="PermissionTypes.InventoryPermissionTypes.stock"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-activity-log">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.stock" :linkId="stockData.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>

          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapState } from 'vuex'

import Utils from '@/utils/utils'
import form from '@/mixins/form'
import StaticDataTable from '@/components/table/staticDataTable.vue'
import SummaryBar from '@/components/summaryBar.vue'
import SummaryBarItem from '@/components/summaryBarItem.vue'
import StockEditDialog from '@/modules/inventory/views/stockEditDialog.vue'
import StockTransactionDataTable from '@/modules/inventory/components/stockTransactionDataTable.vue'
import DocumentList from '@/components/documentList.vue'
import EventBus from '@/utils/eventBus'

import StockTemplateEditor from '@/modules/inventory/components/stockTemplateEditor.vue'
import StockAssemblyEditor from '@/modules/inventory/components/stockAssemblyEditor.vue'
import StockSupplierMatrix from '@/modules/inventory/components/stockSupplierMatrix.vue'

import GlassBlockListDialog from '@/modules/inventory/views/glassBlockListDialog.vue'
import GlassInvBinListDialog from '@/modules/inventory/views/glassInvBinListDialog.vue'
import GlassSizePODialog from '@/modules/inventory/views/glassSizePODialog.vue'
import GlassSizeOrderDialog from '@/modules/inventory/views/glassSizeOrderDialog.vue'
import GlassSizeDialog from '@/modules/inventory/views/glassSizeDialog.vue'
import GlassSizeMovingAverageDialog from '@/modules/inventory/views/glassSizeMovingAverageDialog.vue'
import GlassProcessedPriceEditDialog from '@/modules/inventory/views/glassProcessedPriceEditDialog.vue'
import InventoryBinContentsDialog from '@/modules/inventory/views/inventoryBinContentsDialog.vue'

import SalesGlassPricingEditor from '@/modules/pricing/components/salesGlassPricingEditor.vue'
import SalesServicePricingEditor from '@/modules/pricing/components/salesServicePricingEditor.vue'
import SalesGeneralPricingEditor from '@/modules/pricing/components/salesGeneralPricingEditor.vue'
import SalesAssemblyPricingEditor from '@/modules/pricing/components/salesAssemblyPricingEditor.vue'
import SalesPriceRequestUpdateDialog from '@/modules/pricing/views/salesPriceRequestUpdateDialog.vue'

import StockProductionSettingsEditor from '@/modules/inventory/components/stockProductionSettingsEditor'
import ProductionGlassStockLinkEditor from '@/modules/production/components/productionGlassStockLinkEditor'
import ProductionGlassServiceStockLinkEditor from '@/modules/production/components/productionGlassServiceStockLinkEditor'
import ProductionGlassStockRouteEditor from '@/modules/production/components/productionGlassStockRouteEditor'

import DefaultEdgeworkLinkEditor from '@/modules/ordereditor/components/defaultEdgeworkLinkEditor'
import StockEdgeworkAllowanceEditor from '@/modules/ordereditor/components/stockEdgeworkAllowanceEditor'

import AnalyticsStockItemDashboard from '@/modules/intelligence/components/analyticsStockItemDashboard'

import DataDisplayMixin from '@/mixins/dataDisplay'
import StockFormatterMixin from '@/modules/inventory/mixins/stockFormatter.js'

import { LinkTypes, InventoryStockTypes, GlassTypeLookup, UOMTypeLookup, UOMTypeUnitsLookup, StockPriceTypes, StockGrainDirectionTypeLookup } from '@/store/types'

export default {
  name: "Stock",
  mixins: [form, DataDisplayMixin, StockFormatterMixin],
  components: {
    StaticDataTable,
    SummaryBar,
    SummaryBarItem,
    SalesGlassPricingEditor,
    SalesServicePricingEditor,
    SalesGeneralPricingEditor,
    SalesAssemblyPricingEditor,
    StockTemplateEditor,
    StockAssemblyEditor,
    StockSupplierMatrix,
    StockTransactionDataTable,
    DocumentList,
    StockProductionSettingsEditor,
    ProductionGlassStockLinkEditor,
    ProductionGlassServiceStockLinkEditor,
    ProductionGlassStockRouteEditor,
    DefaultEdgeworkLinkEditor,
    StockEdgeworkAllowanceEditor,
    AnalyticsStockItemDashboard
  },
  data: () => {
    return {
      loading: false,
      breadcrumbs: [
        { title: "Stock", link: "/inventory/stock" },
        { title: "Stock" }
      ],
      glassTypeOptions: GlassTypeLookup,
      stockData: {},

      editingPricing: false,
      stockPricingSubmitting: false,

      editingTemplate: false,
      stockTemplateSubmitting: false,

      editingAssembly: false,
      stockAssemblySubmitting: false,

      editingProductionSettings: false,
      editingProductionSettingsSubmitting: false,

      productionConfigData: {},
      editingProductionLinks: false,
      stockProductionLinksSubmitting: false,

      editingProductionRouting: false,
      stockProductionRoutingSubmitting: false,

      orderEditorConfigData: [],
      editingOrderConfig: false,
      stockOrderEditorSubmitting: false,

      orderEditorEdgeworkSettings: {
        form: {},
        editing: false,
        submitting: false,
        edgeworkAllowanceData: []
      },

      isContainerMatrixDisplayPrice: true,
      isProcessedMatrixDisplayPrice: true,
      isServiceMatrixDisplayPrice: true,

      showEmptyGlassLevels: false
    };
  },
  created() {
    this.LinkTypes = LinkTypes
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.permissionsUpdated, this.permissionsUpdated)
    if(this.isPermissionDataLoaded()) {
      this.permissionsUpdated()
    }
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.reloadData()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.permissionsUpdated, this.permissionsUpdated)
  },
  computed: {
    ...mapState({
      accountCurrencyCode: state => state.account.currencyCode,
    }),
    avatarImage() {
      var glassImgPath = '/glass/img'
      switch(this.stockData.type) {
        case InventoryStockTypes.glass: return `${glassImgPath}/stock-overview-glass.png`
        case InventoryStockTypes.glassService: return `${glassImgPath}/stock-overview-glassService.png`
        case InventoryStockTypes.template: return `${glassImgPath}/stock-overview-template.png`
        case InventoryStockTypes.assembly: return `${glassImgPath}/stock-overview-assembly.png`
        case InventoryStockTypes.general: return `${glassImgPath}/stock-overview-general.png`
      }
      return ''
    },
    activityStreamURL() {
      return `/inventory/stock/${this.$route.params.id}/activitystream`
    },
    stockTypeIsGlass() { return this.stockData.type == InventoryStockTypes.glass },
    stockTypeIsGlassService() { return this.stockData.type == InventoryStockTypes.glassService },
    stockTypeIsTemplate() { return this.stockData.type == InventoryStockTypes.template },
    stockTypeIsAssembly() { return this.stockData.type == InventoryStockTypes.assembly },
    stockTypeIsGeneral() { return this.stockData.type == InventoryStockTypes.general },

    isStockUsingGrain() {
      return this.stockData.isStockUsingGrain
    },

    uomDisplay() {
      return this.getLookupName(UOMTypeLookup, this.stockData.uomType)
    },
    uomDisplayUnits() {
      return Utils.getLookupName(UOMTypeUnitsLookup, this.stockData.uomType)
    },
    combinedBlockList() {
      return []
    },
    totalSheets() {
      return this.stockData.stockLevelSummary.totalSheets - this.stockData.stockLevelSummary.totalOffcuts
    },
    totalBlockSheets() {
      return this.stockData.stockLevelSummary.totalBlockSheets
    },
    totalBlocks() {
      return this.stockData.stockLevelSummary.totalBlocks
    },
    totalSheetStock() {
      return this.stockData.stockLevelSummary.totalSheetStock
    },
    totalOffcuts() {
      return this.stockData.stockLevelSummary.totalOffcuts
    },
    totalOrderEntryStock() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalOrderEntryStock
      }, 0)
    },
    totalOffcutOrderEntryStock() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalOffcutOrderEntryStock
      }, 0)
    },
    totalOnOrder() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalOnOrder
      }, 0)
    },
    totalOptimiserAllocated() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalOptimiserAllocated
      }, 0)
    },
    totalOffcutOptimiserAllocated() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalOffcutOptimiserAllocated
      }, 0)
    },
    totalAvailable() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalAvailable
      }, 0)
    },
    totalOffcutAvailable() {
      if(!this.stockData.glassSizeList)
        return 0

      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.summary.totalOffcutAvailable
      }, 0)
    },
    totalGlassArea() {
      return this.$options.filters.formatDecimal(this.stockData.stockLevelSummary.totalGlassArea, 2)
    },
    sheetPercentage() {
      if(this.stockData.stockLevelSummary.totalSheetArea == 0)
        return 0
      return Math.round((this.stockData.stockLevelSummary.totalSheetArea / this.stockData.stockLevelSummary.totalGlassArea) * 100.0)
    },
    blockPercentage() {
      if(this.stockData.stockLevelSummary.totalBlockArea == 0)
        return 0
      return Math.round((this.stockData.stockLevelSummary.totalBlockArea / this.stockData.stockLevelSummary.totalGlassArea) * 100.0)
    },
    offcutPercentage() {
      if(this.stockData.stockLevelSummary.totalOffcutArea == 0)
        return 0
      return Math.round((this.stockData.stockLevelSummary.totalOffcutArea / this.stockData.stockLevelSummary.totalGlassArea) * 100.0)
    },
    glassLevelCount() {
      if(this.stockData.glassSizeList == null)
        return 0
      return this.stockData.glassSizeList.reduce((count, size) => {
        return count + size.glassLevels.length
      }, 0)
    },
    isAnalyticsDashboardAvailable() {
      if(this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceInventory) || this.userHasReadPermission(this.PermissionTypes.IntelligencePermissionTypes.intelligenceInventory))
        return true
      return false
    }

  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.loading = true

      this.$apiService.inventoryService.getStock(this.$route.params.id)
      .then(response => {
        this.$data.stockData = response.data;

        this.$store.commit("page/setPageTitle", this.$data.stockData.code);
      })
      .finally(() => {
        this.loading = false
      })

      // Production details
      this.$apiService.productionService.getStockProductionConfig(this.$route.params.id)
      .then(response => {
        this.$data.productionConfigData = response.data
      })
      .catch(err => {
        console.dir(err)
      })

      // Order details
      Promise.all([
        this.$apiService.orderEditorService.getStockOrderEditorDefaultEdgeworkList(this.$route.params.id),
        this.$apiService.orderEditorService.getStockOrderEditorEdgeworkAllowanceList(this.$route.params.id)
      ])
      .then(results => {
        this.$data.orderEditorConfigData = results[0].data
        this.$data.orderEditorEdgeworkSettings.edgeworkAllowanceData = results[1].data
      })
      .catch(err => {
        console.dir(err)
      })
    },
    permissionsUpdated() {
      if(!this.isAnalyticsDashboardAvailable) {
        this.$nextTick(() => {
          this.$refs.stockItemTab.show()
        })
      }
    },

    editStock() {
      this.displayModal(StockEditDialog, { 
        stockId: this.$data.stockData.id,
      },
      this.$data.stockData)
      .then((success) => {
        if(success) {
          this.successToast('Stock Updated')
          this.reloadData()
        }
      })
    },
    archiveStock() {
      this.confirmDeleteMessage('Archive Stock?', 'Are you sure you want to archive this stock?')
      .then(success => {
        if(success) {
          this.$apiService.inventoryService.archiveStock(this.$data.stockData.id).then(response => {
            this.successToast('Stock Archived')
            this.reloadData()
          })
          // TODO: Test error response
        }
      })
    },
    restoreStock() {
      this.confirmDeleteMessage('Restore Contact?', 'Are you sure you want to restore this stock?')
      .then(success => {
        if(success) {
          this.$apiService.inventoryService.restoreStock(this.$data.stockData.id).then(response => {
            this.successToast('Stock Restored')
            this.reloadData()
          })
          // TODO: Test error response
        }
      })
    },
    submitPricingEdit() {
      this.displayModal(SalesPriceRequestUpdateDialog, {
      }, this.$data.stockData)
      .then((success) => {
        if(success) {
          this.successToast('Pricing Request Submitted')
          this.editingPricing = false
          this.reloadData()
        }
      })
    },
    cancelPricingEditing() {
      this.editingPricing = false

      // Refresh the stock
      this.reloadData()
    },

    // Template
    submitTemplateEdit() {
      this.$data.stockTemplateSubmitting = true

      var updateData = {
        templateList: this.$data.stockData.templateList
      }

      this.$apiService.inventoryService.updateStock(this.$data.stockData.id, updateData)
      .then(response => {
        this.editingTemplate = false
        this.successToast('Stock Template Updated')
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock template')                
      })
      .finally(() => {
        this.$data.stockTemplateSubmitting = false
      })
    },
    cancelTemplateEditing() {
      this.editingTemplate = false

      // Refresh the stock
      this.reloadData()
    },

    // Assembly
    submitAssemblyEdit() {
      this.$data.stockAssemblySubmitting = true

      var updateData = {
        assemblyData: this.$data.stockData.assemblyData
      }

      this.$apiService.inventoryService.updateStock(this.$data.stockData.id, updateData)
      .then(response => {
        this.editingAssembly = false
        this.successToast('Stock Assembly Updated')
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock assembly')                
      })
      .finally(() => {
        this.$data.stockAssemblySubmitting = false
      })
    },
    cancelAssemblyEditing() {
      this.editingAssembly = false

      // Refresh the stock
      this.reloadData()
    },

    // Production
    submitProductionSettingEdit() {
      this.editingProductionSettingsSubmitting = true

      let updateData = _.pick(this.stockData, ['glassMinOffcutHeight', 'glassMinOffcutWidth', 'panelTrim', 'offcutEdgeTrim', 'panelEdgeOnSheetEdgeEnabled', 'panelEdgeOnOffcutEdgeEnabled', 'glassTrimTop', 'glassTrimRight', 'glassTrimBottom', 'glassTrimLeft'])
      Object.keys(updateData).forEach(key => {
        if(updateData[key] === '')
          updateData[key] = null
      })

      this.$apiService.inventoryService.updateStock(this.$data.stockData.id, updateData)
      .then(() => {
        this.editingProductionSettings = false
        this.successToast('Production Settings Updated')        
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock production settings')
      })
      .finally(() => {
        this.editingProductionSettingsSubmitting = false
      })
    },
    cancelProductionSettingEditing() {
      this.editingProductionSettings = false
      this.reloadData()
    },

    submitProductionLinkEdit() {
      this.$data.stockProductionLinksSubmitting = true

      var updateData = {
        productionStageOverrideList: this.$data.productionConfigData.productionStageOverrideList,
        productionStationOverrideList: this.$data.productionConfigData.productionStationOverrideList,
      }

      this.$apiService.productionService.updateStockProductionConfig(this.$data.stockData.id, updateData)
      .then(response => {
        this.stockProductionLinksSubmitting = false
        this.editingProductionLinks = false
        this.successToast('Production Links Updated')
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock production links')
      })
      .finally(() => {
        this.$data.stockProductionLinksSubmitting = false
      })
    },
    cancelProductionLinkEditing() {
      this.editingProductionLinks = false
      this.reloadData()
    },

    submitProductionRouteEdit() {
      this.$data.stockProductionRoutingSubmitting = true

      var updateData = {
        productionStationRouteList: this.$data.productionConfigData.productionStationRouteList
      }

      this.$apiService.productionService.updateStockProductionConfig(this.$data.stockData.id, updateData)
      .then(response => {
        this.stockProductionRoutingSubmitting = false
        this.editingProductionRouting = false
        this.successToast('Production Routes Updated')
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock production routing')
      })
      .finally(() => {
        this.$data.stockProductionLinksSubmitting = false
      })
    },
    cancelProductionRouteEditing() {
      this.editingProductionRouting = false
      this.reloadData()
    },

    // Order
    submitOrderEditorEdit() {
      this.$data.stockOrderEditorSubmitting = true

      var updateData = {
        edgeworkLinkList: this.orderEditorConfigData
      }

      this.$apiService.orderEditorService.updateStockOrderEditorDefaultEdgeworkList(this.$data.stockData.id, updateData)
      .then(response => {
        this.stockOrderEditorSubmitting = false
        this.editingOrderConfig = false
        this.successToast('Order Settings Updated')
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stock order settings')
      })
      .finally(() => {
        this.$data.stockOrderEditorSubmitting = false
      })
    },
    cancelOrderEditorEditing() {
      this.editingOrderConfig = false
      this.reloadData()
    },

    // Order Editor - Edgework
    editOrderEditorEdgework() {
      this.orderEditorEdgeworkSettings.form = this.clearFormResponse()
      this.orderEditorEdgeworkSettings.editing = true
    },
    cancelOrderEditorEdgeworkEditing() {
      this.orderEditorEdgeworkSettings.form = this.clearFormResponse()
      this.orderEditorEdgeworkSettings.editing = false
      this.reloadData()
    },
    submitOrderEditorEdgeworkEditing() {
      this.orderEditorEdgeworkSettings.submitting = true

      var updateData = {
        stockEdgeworkAllowances: this.orderEditorEdgeworkSettings.edgeworkAllowanceData
      }

      this.$apiService.orderEditorService.updateStockOrderEditorEdgeworkAllowanceList(this.$data.stockData.id, updateData)
      .then(response => {
        this.orderEditorEdgeworkSettings.editing = false
        this.successToast('Edgework Updated')
      })
      .catch(err => {
        this.orderEditorEdgeworkSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.orderEditorEdgeworkSettings.submitting = false
      })
    },


    // Glass Sizes - Block List
    displayGlassSizeBlocks(glassSize) {
      this.displayModal(GlassBlockListDialog, { 
        stockId: this.$data.stockData.id,
        glassSizeId: glassSize.id
      })
      .then(() => {})
    },
    // Glass Sizes - Inventory Bin List
    displayGlassSizeInventoryBins(glassSize) {
      this.displayModal(GlassInvBinListDialog, { 
        stockId: this.$data.stockData.id,
        glassSizeId: glassSize.id
      })
      .then(() => {})
    },
    // Glass Sizes - Orders
    displayGlassSizeOrders(glassSize) {
      this.displayModal(GlassSizeOrderDialog, {
        stockId: this.$data.stockData.id, 
        glassSizeId: glassSize.id
      })
      .then(() => {})
    },
    // Glass Sizes - Purchase Orders
    displayGlassSizePurchaseOrders(glassSize) {
      this.displayModal(GlassSizePODialog, {
        stockId: this.$data.stockData.id, 
        glassSizeId: glassSize.id
      })
      .then(() => {})
    },

    // Glass Offcuts
    printGlassOffcutLabel(glassLevel) {
      this.$apiService.inventoryService.generatePrintOffcutLabelURL(glassLevel.id, { download: true, label: true })
      .then(response => {
        window.location.href = response.data.url
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Print Error')
      })
    },
    filteredAccountList(glassSize) {
      if(this.showEmptyGlassLevels)
        return glassSize.accLocationSummaryList

      return glassSize.accLocationSummaryList.filter(accInfo => {
        return (this.filteredAccountGlassLevelList(accInfo).length > 0)
      })
    },
    filteredAccountGlassLevelList(accountLocSummary) {
      if(this.showEmptyGlassLevels)
        return accountLocSummary.glassLevels

      // Hide all levels have zero values for all fields
      return accountLocSummary.glassLevels.filter(levelInfo => {
        // If no values, hide it
        if(levelInfo.totalBlocks == 0 &&
           levelInfo.totalSheetStock == 0 &&
           levelInfo.totalOrderEntryStock == 0 &&
           levelInfo.totalOptimiserAllocated == 0 &&
           levelInfo.totalAvailable == 0 &&
           levelInfo.totalOnOrder == 0) {
          return false
        }

        return true
      })
    },
    filteredGlassLevelList(glassSize) {
      if(this.showEmptyGlassLevels)
        return glassSize.glassLevels

      // Hide all levels have zero values for all fields
      return glassSize.glassLevels.filter(level => {
        // If no values, hide it
        if(level.summary.totalBlocks == 0 &&
           level.summary.totalSheetStock == 0 &&
           level.summary.totalOrderEntryStock == 0 &&
           level.summary.totalOptimiserAllocated == 0 &&
           level.summary.totalAvailable == 0 &&
           level.summary.totalOnOrder == 0) {
          return false
        }

        return true
      })
    },


    // Stock Management
    editGlassSizeMovingAverage(glassSize) {
      this.displayModal(GlassSizeMovingAverageDialog, { 
        stock: this.$data.stockData,
        stockId: this.$data.stockData.id,
      }, glassSize)
      .then((success) => {
        if(success) {
          this.successToast('Glass Size Moving Average Updated')
          this.reloadData()
        }
      })
    },
    createGlassSize() {
      this.displayModal(GlassSizeDialog, { 
        isCreateMode: true,
        stock: this.$data.stockData,
        stockId: this.$data.stockData.id,
      })
      .then((success) => {
        if(success) {
          this.successToast('Glass Size Created')
          this.reloadData()
        }
      })
    },
    editGlassSize(glassSize) {
      this.displayModal(GlassSizeDialog, { 
        isCreateMode: false,
        stock: this.$data.stockData,
        stockId: this.$data.stockData.id,
      }, glassSize)
      .then((success) => {
        if(success) {
          this.successToast('Glass Size Updated')
          this.reloadData()
        }
      })
    },
    setGlassSizeActive(glassSizeId, isActive) {
      this.$apiService.inventoryService.updateGlassSize(this.$data.stockData.id, glassSizeId, { isActive: isActive })
      .then(response => {
        this.successToast('Glass Size Updated')
        this.reloadData()
      })
    },
    isGlassSizePurchasingPaused(glassSize) {
      if(!glassSize.isPurchasingPaused)
        return false

      // Check if pause has expired
      if(glassSize.purchasingPauseExpiryDate) {
        return moment(glassSize.purchasingPauseExpiryDate).isAfter(moment())
      }
      return false
    },
    editPriceGroupSupplier(priceGroupSupplier) {
      var itemInfo = {
        preferredSupplierId: null
      }
      if(priceGroupSupplier.glassPriceGroupSupplier)
        itemInfo.preferredSupplierId = priceGroupSupplier.glassPriceGroupSupplier.preferredSupplierId

      this.displayModal(GlassProcessedPriceEditDialog, { 
        stockId: this.$data.stockData.id,
        priceGroupId: priceGroupSupplier.id
      }, itemInfo)
      .then((success) => {
        if(success) {
          this.successToast('Price Group Supplier Updated')
          this.reloadData()
        }
      })
    },
    setServicePreferredSupplier(stockPriceGroupId, contactId) {
      var updateData = {
        priceType: StockPriceTypes.servicePriceRange,
       contactId: contactId,
       stockPriceGroupId: stockPriceGroupId
      }
      this.$apiService.pricingService.updatePreferredSupplierForStock(this.$data.stockData.id, updateData)
      .then(response => {
        this.successToast('Preferred Supplier Updated')
        this.reloadData()
      })
      .catch(err => {
        // TODO: Handle this better
        console.error(err)
      })
    },
    setGeneralPreferredSupplier(contactId) {
      var updateData = {
        priceType: StockPriceTypes.general,
       contactId: contactId
      }
      this.$apiService.pricingService.updatePreferredSupplierForStock(this.$data.stockData.id, updateData)
      .then(response => {
        this.successToast('Preferred Supplier Updated')
        this.reloadData()
      })
      .catch(err => {
        // TODO: Handle this better
        console.error(err)
      })
    },
    setAssemblyPreferredSupplier(contactId) {
      var updateData = {
        priceType: StockPriceTypes.assembly,
       contactId: contactId
      }
      this.$apiService.pricingService.updatePreferredSupplierForStock(this.$data.stockData.id, updateData)
      .then(response => {
        this.successToast('Preferred Supplier Updated')
        this.reloadData()
      })
      .catch(err => {
        // TODO: Handle this better
        console.error(err)
      })
    },

    // View Inventory
    viewInventoryBinContents(inventoryBin) {
      this.displayModal(InventoryBinContentsDialog, { 
        inventoryBinId: inventoryBin.id
      })
      .then(() => {
      })
    }
  }
}

</script>